import { FC, ReactNode, useEffect, useMemo, useState } from 'react'

import cn from 'clsx'
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form'

import { toDecimalPrecision } from '@App/helpers/toDecimalPrecision'
import { useAppSelector } from '@App/hooks'

import { getTickerData } from '@Redux/slices/sockets/PublicServiceConnectionSlice'

import { Tabs } from '@UI/crypto_exchange/desktop/components/common/Tabs'
import { InputWithButtonControl } from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/components/InputWithButtonControl/InputWithButtonControl'
import { PriceAlertCarousel } from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/components/PriceAlertCarousel/PriceAlertCarousel'
import { ALERT_TYPE_TABS } from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/constants'
import { AlertType, FormData } from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/types'
import { TradeInput } from '@UI/crypto_exchange/desktop/pages/spot/components/SpotOrderTrade/components/TradeInput'
import { ORDER_ACTION_TYPE } from '@UI/crypto_exchange/desktop/pages/spot/components/SpotOrderTrade/components/types'

import s from './styles.module.scss'

type AlertFormProps = {
  targetCurrency?: string
  sourceCurrency?: string
  className?: string
  onSubmit: SubmitHandler<FormData>
  children: ReactNode
  prevData?: AlertType
}

export const AlertForm: FC<AlertFormProps> = ({
  sourceCurrency,
  targetCurrency,
  onSubmit,
  children,
  prevData,
  className,
}) => {
  const [isActiveTabTouched, setIsActiveTabTouched] = useState(false)
  const [bid, setBid] = useState<number | null>()
  const [ask, setAsk] = useState<number | null>()
  const tickerData = useAppSelector((state) => getTickerData(state, sourceCurrency || '', targetCurrency || ''))

  const { setValue, control, handleSubmit, getValues } = useFormContext<FormData>()

  const [activeTab, setActiveTab] = useState<ORDER_ACTION_TYPE>(
    (getValues('side') as ORDER_ACTION_TYPE) || ALERT_TYPE_TABS[0].value,
  )

  const step = useMemo(() => toDecimalPrecision(bid || ask || 0), [ask, activeTab])

  const addPercentToAlert = (percent: number) => {
    const value = activeTab === ORDER_ACTION_TYPE.SELL ? tickerData.bid : tickerData.ask
    if (value == null || percent == null) return

    const result = value + (value * percent) / 100
    const decimalPlaces = step.toString().split('.')?.[1]?.length
    const formatedResult = result.toFixed(decimalPlaces >= 2 ? decimalPlaces : 2)
    setValue('execution_price', formatedResult)
  }

  const clearNoteInput = () => {
    setValue('note', '')
  }

  const setActiveTabHandler = (val: ORDER_ACTION_TYPE) => {
    setActiveTab(val)
    setValue('side', val)
    setIsActiveTabTouched(true)
  }

  useEffect(() => {
    if (prevData && prevData.execution_price && !isActiveTabTouched) {
      setBid(+prevData.execution_price)
      setAsk(+prevData.execution_price)
    } else if (!tickerData.bid || !tickerData.ask) {
      setValue('execution_price', '')
    } else {
      setBid(tickerData.bid)
      setAsk(tickerData.ask)
      setValue('execution_price', activeTab === ORDER_ACTION_TYPE.SELL ? String(tickerData.bid) : String(tickerData.ask))
    }
    setValue('note', prevData?.note || '')
  }, [sourceCurrency, targetCurrency, activeTab])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cn(s.priceAlertsContainer, className)}>
      <Tabs<ORDER_ACTION_TYPE>
        tabs={ALERT_TYPE_TABS}
        activeTab={activeTab}
        tabsFillSpace
        setActiveTab={setActiveTabHandler}
      />

      <Controller
        name="execution_price"
        control={control}
        render={({ field, fieldState }) => (
          <TradeInput type='number' {...field} error={fieldState.error?.message} showControls step={step} />
        )}
      />

      <PriceAlertCarousel addPercentToAlert={addPercentToAlert} />

      <Controller
        name="note"
        control={control}
        render={({ field, fieldState }) => (
          <InputWithButtonControl
            clearInput={clearNoteInput}
            hideInputControl={!!prevData}
            {...field}
            error={fieldState.error?.message}
            placeholder="Note"
          />
        )}
      />

      {children}
    </form>
  )
}
